import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import produce from 'immer';
import { omit } from 'underscore';

import { queryKey } from '~teacher/hook/activity/queryKey';

import { deleteStudentScore } from '../../api/studentScore';
import { ActivityResponse } from './activity';

export function removeStudentScore({
  activity,
  questionId,
  studentId,
}: {
  activity: Pick<ActivityResponse, 'questionScores'>;
  questionId: number;
  studentId: number;
}) {
  return produce(activity, (nextActivity) => {
    const questionScore = nextActivity.questionScores[questionId];
    if (!questionScore) return;
    questionScore.studentScores = omit(
      questionScore.studentScores,
      `${studentId}`
    );

    if (Object.keys(questionScore.studentScores).length === 0) {
      nextActivity.questionScores = omit(
        nextActivity.questionScores,
        `${questionId}`
      );
    }
  });
}

function updateStudentScoreCache({
  queryClient,
  activityInstanceId,
  questionId,
  studentId,
}: {
  queryClient: QueryClient;
  activityInstanceId: number;
  questionId: number;
  studentId: number;
}) {
  queryClient.setQueryData(
    queryKey.byId(activityInstanceId),
    (activity: ActivityResponse) => {
      return removeStudentScore({ activity, questionId, studentId });
    }
  );
}

export function useDeleteStudentScore({
  activityInstanceId,
  questionId,
}: {
  activityInstanceId: number;
  questionId: number;
}) {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      questionScoreId,
      isRostered,
      studentId,
    }: {
      questionScoreId: number;
      isRostered: boolean;
      studentId: number;
    }) =>
      deleteStudentScore({
        questionScoreId,
        isRostered,
        studentId,
      }),
    {
      onSuccess: async (data, { studentId }) => {
        updateStudentScoreCache({
          queryClient: queryClient,
          activityInstanceId: activityInstanceId,
          questionId: questionId,
          studentId: studentId,
        });
        await queryClient.invalidateQueries(queryKey.byId(activityInstanceId));
      },
    }
  );
}
